import { useState, useEffect, useMemo, useRef } from 'react';
import api from '../services/api';
import './TaskConversation.css';

const CHAT_API_BASE = '/api/chat';

// Authenticated fetch wrapper - adds Bearer token to all requests
const authFetch = async (url, options = {}) => {
  const token = await api.ensureAuthenticated();
  const headers = { ...(options.headers || {}), 'Authorization': `Bearer ${token}` };
  return fetch(url, { ...options, headers });
};

const getCurrentUser = () => {
  try { return JSON.parse(sessionStorage.getItem('user') || 'null'); }
  catch (e) { return null; }
};

// Co-workers for the @mention dropdown, fetched once and shared across every
// reply/edit input (same source as the Notes @mention widget).
let coworkersPromise = null;
const loadCoworkers = () => {
  if (!coworkersPromise) {
    coworkersPromise = (async () => {
      try {
        const res = await authFetch(`${CHAT_API_BASE}/coworkers.php`);
        const data = await res.json();
        return data.success ? (data.data || []) : [];
      } catch (e) { return []; }
    })();
  }
  return coworkersPromise;
};

// A single-line text input with an @mention co-worker dropdown. Typing "@" (at
// the start or after whitespace) opens a filtered list; pick with the mouse or
// Arrow keys + Enter/Tab. Enter (no dropdown) submits; Escape cancels.
const MentionInput = ({ value, onChange, onSubmit, onCancel, placeholder, disabled, autoFocus }) => {
  const [coworkers, setCoworkers] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [anchor, setAnchor] = useState(0);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { let live = true; loadCoworkers().then(list => { if (live) setCoworkers(list); }); return () => { live = false; }; }, []);

  const filtered = coworkers
    .filter(cw => cw && (cw.name?.toLowerCase().includes(search) || cw.user_name?.toLowerCase().includes(search)))
    .slice(0, 8);

  const detect = (val, cursor) => {
    const before = val.substring(0, cursor);
    const at = before.lastIndexOf('@');
    if (at !== -1 && !before.substring(at).includes(' ') &&
        (at === 0 || before[at - 1] === ' ' || before[at - 1] === '\n')) {
      setSearch(before.substring(at + 1).toLowerCase());
      setAnchor(at);
      setActive(0);
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    detect(e.target.value, e.target.selectionStart);
  };

  const pick = (cw) => {
    const caret = inputRef.current ? inputRef.current.selectionStart : value.length;
    const before = value.substring(0, anchor);
    const after = value.substring(caret);
    const inserted = `@${cw.name} `;
    onChange(before + inserted + after);
    setOpen(false);
    setTimeout(() => {
      if (inputRef.current) {
        const pos = before.length + inserted.length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (open && filtered.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => (i + 1) % filtered.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(i => (i - 1 + filtered.length) % filtered.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(filtered[active]); return; }
      if (e.key === 'Escape')    { e.preventDefault(); setOpen(false); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit && onSubmit(); return; }
    if (e.key === 'Escape' && onCancel) { e.preventDefault(); onCancel(); }
  };

  return (
    <div className="task-conv-mention-wrap">
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      />
      {open && filtered.length > 0 && (
        <div className="task-conv-mention-dropdown">
          {filtered.map((cw, i) => (
            <button
              key={cw.contact_id || cw.user_id || cw.name}
              type="button"
              className={`task-conv-mention-option ${i === active ? 'active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(cw); }}
            >
              <span className="task-conv-mention-name">{cw.name}</span>
              {cw.user_name && <span className="task-conv-mention-username">@{cw.user_name}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Tasks tab rendered as a chat-style conversation, grouped by @mentioned
// recipient (the assigned_user). Each task is a message from its creator;
// task-responses.php replies hang under each task, immutable by the reader.
const TaskConversations = ({ tasks, currentUser, onEditTask, onTaskChanged }) => {
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString() : '';

  // Group tasks by assigned_user_id (falls back to 'unassigned').
  const groups = useMemo(() => {
    const map = new Map();
    tasks.forEach(t => {
      const a = t.attributes || {};
      const key = a.assigned_user_id || 'unassigned';
      const name = a.assigned_user_name && a.assigned_user_name.trim() ? a.assigned_user_name.trim() : 'Unassigned';
      if (!map.has(key)) map.set(key, { key, name, tasks: [] });
      map.get(key).tasks.push(t);
    });
    // Newest task first within each group; groups sorted alphabetically by name.
    const arr = Array.from(map.values());
    arr.forEach(g => g.tasks.sort((a, b) => {
      const ad = a.attributes?.date_entered || '';
      const bd = b.attributes?.date_entered || '';
      return bd.localeCompare(ad);
    }));
    arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [tasks]);

  const handleDelete = async (task) => {
    if (!window.confirm('Delete this task? Responses on it will remain in the record.')) return;
    try {
      const res = await authFetch(`${CHAT_API_BASE}/project-tasks.php?id=${encodeURIComponent(task.id)}&user_id=${encodeURIComponent(currentUser?.id || '')}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        alert(data.message || 'Failed to delete task');
        return;
      }
      if (onTaskChanged) onTaskChanged();
    } catch (e) {
      alert('Failed to delete task: ' + e.message);
    }
  };

  return (
    <div className="task-conversations">
      {groups.map(group => (
        <div key={group.key} className="task-conv-group">
          <div className="task-conv-group-header">
            <span className="task-conv-recipient">@{group.name}</span>
            <span className="task-conv-count">{group.tasks.length}</span>
          </div>
          <div className="task-conv-thread">
            {group.tasks.map(task => (
              <TaskConversationBubble
                key={task.id}
                task={task}
                currentUser={currentUser}
                onEdit={onEditTask}
                onDelete={handleDelete}
                onResponsesChanged={onTaskChanged}
                fmtDate={fmtDate}
                fmtDateTime={fmtDateTime}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// One response bubble. Author gets Edit/Delete; other viewers get read-only.
const TaskResponseBubble = ({ response, currentUser, onUpdated, onDeleted, fmtDateTime }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(response.content || '');
  const [busy, setBusy] = useState(false);

  const isAuthor = currentUser && response.user_id && (currentUser.id === response.user_id);

  const save = async () => {
    const content = draft.trim();
    if (!content) return;
    setBusy(true);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/task-responses.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: response.id, content, user_id: currentUser?.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        onUpdated({ ...response, content });
        setEditing(false);
      } else {
        alert(data.message || 'Failed to update response');
      }
    } catch (e) {
      alert('Failed to update response: ' + e.message);
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm('Delete this response?')) return;
    setBusy(true);
    try {
      const qs = `id=${encodeURIComponent(response.id)}&user_id=${encodeURIComponent(currentUser?.id || '')}`;
      const res = await authFetch(`${CHAT_API_BASE}/task-responses.php?${qs}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        onDeleted(response.id);
      } else {
        alert(data.message || 'Failed to delete response');
      }
    } catch (e) {
      alert('Failed to delete response: ' + e.message);
    }
    setBusy(false);
  };

  return (
    <div className="task-conv-bubble task-conv-bubble-reply">
      <div className="task-conv-reply-meta">
        <span className="task-conv-reply-author">{response.user_name || 'Someone'}</span>
        <span className="task-conv-reply-time">{fmtDateTime(response.date_entered)}</span>
      </div>
      {editing ? (
        <div className="task-conv-reply-edit">
          <MentionInput
            value={draft}
            onChange={setDraft}
            onSubmit={save}
            onCancel={() => { setEditing(false); setDraft(response.content || ''); }}
            disabled={busy}
            autoFocus
          />
          <div className="task-conv-reply-edit-actions">
            <button className="task-conv-btn" onClick={save} disabled={busy || !draft.trim()}>Save</button>
            <button className="task-conv-btn" onClick={() => { setEditing(false); setDraft(response.content || ''); }} disabled={busy}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="task-conv-reply-body">{response.content}</div>
          {isAuthor && (
            <div className="task-conv-reply-actions">
              <button className="task-conv-btn" onClick={() => setEditing(true)} disabled={busy}>Edit</button>
              <button className="task-conv-btn task-conv-btn-danger" onClick={remove} disabled={busy}>Delete</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const TaskConversationBubble = ({ task, currentUser, onEdit, onDelete, onResponsesChanged, fmtDate, fmtDateTime }) => {
  const a = task.attributes || {};
  const [responses, setResponses] = useState(task.responses || []);
  const [replyText, setReplyText] = useState('');
  const [posting, setPosting] = useState(false);

  // Keep local responses in sync when the parent refetches tasks.
  useEffect(() => { setResponses(task.responses || []); }, [task.responses]);

  const isCreator = currentUser && (currentUser.id === a.created_by);
  const priorityKey = (a.priority || 'none').toLowerCase().replace(/\s+/g, '-');

  const postReply = async () => {
    const content = replyText.trim();
    if (!content) return;
    setPosting(true);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/task-responses.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Pass the logged-in user id — Bearer auth is a shared apiuser here.
        body: JSON.stringify({ task_id: task.id, content, user_id: currentUser?.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.data) {
        setResponses(prev => [...prev, data.data]);
        setReplyText('');
        if (onResponsesChanged) onResponsesChanged();
      } else {
        alert(data.message || 'Failed to post reply');
      }
    } catch (e) {
      alert('Failed to post reply: ' + e.message);
    }
    setPosting(false);
  };

  return (
    <div className="task-conv-item">
      <div className="task-conv-bubble task-conv-bubble-task">
        <div className="task-conv-title">{a.name || 'Untitled Task'}</div>
        <div className="task-conv-meta">
          <span className="task-conv-mention">@{a.assigned_user_name && a.assigned_user_name.trim() ? a.assigned_user_name : 'Unassigned'}</span>
          <span className={`task-conv-priority priority-${priorityKey}`}>{a.priority || 'No Priority'}</span>
          {a.date_entered && (
            <span className="task-conv-date">Created {fmtDate(a.date_entered)}</span>
          )}
          {a.date_due && (
            <span className="task-conv-date task-conv-due">Due {fmtDate(a.date_due)}</span>
          )}
        </div>
        {a.description && a.description.trim() && (
          <div className="task-conv-desc">{a.description}</div>
        )}
        {isCreator && (
          <div className="task-conv-actions">
            <button className="task-conv-btn" onClick={() => onEdit(task)}>Edit</button>
            <button className="task-conv-btn task-conv-btn-danger" onClick={() => onDelete(task)}>Delete</button>
          </div>
        )}
      </div>
      {responses.length > 0 && (
        <div className="task-conv-replies">
          {responses.map(r => (
            <TaskResponseBubble
              key={r.id}
              response={r}
              currentUser={currentUser}
              onUpdated={updated => setResponses(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x))}
              onDeleted={deletedId => setResponses(prev => prev.filter(x => x.id !== deletedId))}
              fmtDateTime={fmtDateTime}
            />
          ))}
        </div>
      )}
      <div className="task-conv-reply-input">
        <MentionInput
          value={replyText}
          onChange={setReplyText}
          onSubmit={postReply}
          placeholder="Reply... (@ to mention)"
          disabled={posting}
        />
        <button className="task-conv-send-btn" onClick={postReply} disabled={posting || !replyText.trim()}>
          {posting ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default TaskConversations;
export { getCurrentUser, TaskConversationBubble };
